import { useState } from 'react';
import toast from 'react-hot-toast';

import { PasswordEyeInput } from '@/components/forms';
import { Button, Spinner } from '@/components/ui';
import { useAuthStore } from '@/stores/useAuthStore';
import { useModalStore } from '@/stores/useModalStore';
import { logger } from '@/lib/logger';
import type { ChangePasswordRequest } from '@/types/auth';

import styles from './ChangePasswordModal.module.css';

export function ChangePasswordModal() {
  const { changePassword } = useAuthStore();
  const { closeModal } = useModalStore();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState<ChangePasswordRequest>({
    old_password: '',
    new_password1: '',
    new_password2: '',
  });
  // Auth check is handled by ProtectedRoute

  const handleFormDataChange = (key: string, value: string) => {
    setFormData((prevState: ChangePasswordRequest) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const validateForm = (formData: ChangePasswordRequest) => {
    const { old_password, new_password1, new_password2 } = formData;

    if (!old_password || !new_password1 || !new_password2) {
      return { isValid: false, message: 'Please complete all fields' };
    }

    // Password confirmation validation
    if (new_password1 !== new_password2) {
      return { isValid: false, message: 'Passwords do not match' };
    }

    // Check if new password is different from old password
    if (old_password === new_password1) {
      return { isValid: false, message: 'The new password must be different from the current one' };
    }

    // Password strength validation (optional)
    if (new_password1.length < 8) {
      return { isValid: false, message: 'The password must be at least 8 characters long' };
    }

    return { isValid: true, message: '' };
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const validation = validateForm(formData);
    if (!validation.isValid) {
      toast.error(validation.message);
      return;
    }

    try {
      setIsSubmitting(true);
      // Note: Passwords are NOT sanitized - they're sent as-is to preserve user intent
      const success = await changePassword(formData);
      if (success) {
        toast.success('Password updated successfully');
        closeModal();
        return;
      }
    } catch (error) {
      logger.error('Change password error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Change Password</h2>
      <form className={styles.form} onSubmit={handleSubmit}>
        <PasswordEyeInput
          name="old_password"
          value={formData.old_password}
          setValue={(value) => handleFormDataChange('old_password', value)}
          label="Current Password"
          placeholder="Enter your current password"
        />
        <PasswordEyeInput
          name="new_password1"
          value={formData.new_password1}
          setValue={(value) => handleFormDataChange('new_password1', value)}
          label="New Password"
          placeholder="Enter your new password"
        />
        <PasswordEyeInput
          name="new_password2"
          value={formData.new_password2}
          setValue={(value) => handleFormDataChange('new_password2', value)}
          label="Confirm Password"
          placeholder="Confirm your new password"
        />

        <div className={styles.actions}>
          <Button
            variant="secondary"
            size="md"
            onClick={closeModal}
            disabled={isSubmitting}
            type="button"
          >
            Cancel
          </Button>
          <Button variant="primary" size="md" type="submit" disabled={isSubmitting}>
            {isSubmitting ? <Spinner variant="secondary" size="sm" /> : 'Update'}
          </Button>
        </div>
      </form>
    </div>
  );
}
